import NoPreviewImage from '@assets/images/no-preview.png';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import { Box, CircularProgress } from '@mui/material';
import TextField, {
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextFieldVariants,
} from '@mui/material/TextField';
import { AutoComplete, MotionImage } from '@shared/atoms';
import { decodeHTMLEntities } from '@utils/general.util';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';

interface LearningOption {
  value: number;
  imageUrl: string | null;
  label: string;
  type: string | null;
}

interface Props {
  learningContentOptions: LearningOption[];
  searchQuery: string;
  searchLoading: boolean;
  onSearchQueryChange: (query: string) => void;
  onSelectionChange: (value: number, query: string, type: string | null) => void;
}

export const LearningContentAutocomplete: React.FC<Props> = ({
  learningContentOptions,
  searchQuery,
  searchLoading,
  onSearchQueryChange,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeDescendant, setActiveDescendant] = useState('');
  const [focusedOption, setFocusedOption] = useState<HTMLLIElement | null>(null);
  const listboxId = 'learning-content-listbox';
  const comboboxId = 'learning-content-combobox';
  const listRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const loadingAnnouncement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loadingAnnouncement.current) {
      if (searchLoading) {
        loadingAnnouncement.current.textContent = 'Loading search results';
      } else if (isOpen && learningContentOptions.length > 0) {
        loadingAnnouncement.current.textContent = `${learningContentOptions.length} search results available. Use up and down arrow keys to navigate.`;
      } else if (isOpen && learningContentOptions.length === 0) {
        loadingAnnouncement.current.textContent = 'No search results found';
      }
    }
  }, [searchLoading, isOpen, learningContentOptions.length]);

  const getOptionId = (index: number) => `learning-option-${index}`;

  useEffect(() => {
    setActiveIndex(-1);
    setActiveDescendant('');
  }, [learningContentOptions]);

  useEffect(() => {
    if (focusedOption && isOpen) {
      focusedOption.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedOption, isOpen]);

  // Handle custom keyboard navigation
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen || learningContentOptions.length === 0) return;

    switch (event.key) {
      case KeyConfigurations.ARROW_DOWN:
        event.preventDefault();
        setActiveIndex((prevIndex) => {
          const newIndex = prevIndex < learningContentOptions.length - 1 ? prevIndex + 1 : 0;
          setActiveDescendant(getOptionId(newIndex));

          return newIndex;
        });
        break;
      case KeyConfigurations.ARROW_UP:
        event.preventDefault();
        setActiveIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : learningContentOptions.length - 1;
          setActiveDescendant(getOptionId(newIndex));

          return newIndex;
        });
        break;
      case KeyConfigurations.ENTER:
        event.preventDefault();
        if (activeIndex >= 0) {
          const selectedOption = learningContentOptions[activeIndex];
          onSelectionChange(selectedOption.value, searchQuery, selectedOption.type);
          setIsOpen(false);
        }

        break;
      case KeyConfigurations.ESCAPE:
        event.preventDefault();
        setIsOpen(false);
        break;
      case KeyConfigurations.TAB:
        setIsOpen(true);
        break;
      case KeyConfigurations.HOME:
        event.preventDefault();
        setActiveIndex(0);
        setActiveDescendant(getOptionId(0));

        break;
      case KeyConfigurations.END: {
        event.preventDefault();
        const lastIndex = learningContentOptions.length - 1;
        setActiveIndex(lastIndex);
        setActiveDescendant(getOptionId(lastIndex));
        break;
      }

      default:
        break;
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const optionElements = listRef.current.querySelectorAll('li[role="option"]');
      if (optionElements[activeIndex]) {
        setFocusedOption(optionElements[activeIndex] as HTMLLIElement);
      }
    }
  }, [activeIndex]);

  const renderCustomOption = (
    props: JSX.IntrinsicAttributes &
      React.ClassAttributes<HTMLLIElement> &
      React.LiHTMLAttributes<HTMLLIElement>,
    option: { imageUrl: string | null; label: string },
    { index }: { index: number },
  ) => {
    const optionId = getOptionId(index);
    const isActive = index === activeIndex;

    return (
      <li
        {...props}
        className={`${styles.learningSearchList} ${isActive ? styles.activeOption : ''}`}
        role="option"
        id={optionId}
        aria-selected={isActive}
        tabIndex={-1} // Options shouldn't be directly tabbable, but should be selectable via arrow keys
        onMouseEnter={() => {
          setActiveIndex(index);
          setActiveDescendant(optionId);
        }}
        onClick={() => {
          const selectedOption = learningContentOptions[index];
          onSelectionChange(selectedOption.value, searchQuery, selectedOption.type);
          setIsOpen(false);
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }}
      >
        {option.imageUrl ? (
          <MotionImage
            src={option.imageUrl}
            alt=""
            className={styles.autocompleteImageOption}
            onError={(e) => {
              e.currentTarget.src = NoPreviewImage;
            }}
            skeletonHeight="60px"
            skeletonWidth="100px"
            shape="rounded"
            aria-hidden="true"
          />
        ) : (
          <div className={styles.autocompleteImageOption} aria-hidden="true" />
        )}
        <div className={styles.contentAutocompleteOption}>
          <h6>{decodeHTMLEntities(option.label)}</h6>
        </div>
      </li>
    );
  };

  return (
    <Box
      role="combobox"
      aria-expanded={isOpen}
      aria-owns={listboxId}
      aria-haspopup="listbox"
      aria-controls={listboxId}
      sx={{ width: '100%' }}
      onKeyDown={handleKeyDown}
    >
      <Box
        className="sr-only"
        aria-live="polite"
        ref={loadingAnnouncement}
        sx={{ display: 'none' }}
      />
      <AutoComplete
        fullWidth
        sx={{ width: '100%', backgroundColor: 'white' }}
        handleHomeEndKeys
        filterOptions={(x: any) => x}
        loading={searchLoading}
        className={styles.searchInput}
        options={learningContentOptions}
        getOptionLabel={(option: { label: string }) => decodeHTMLEntities(option.label)}
        inputValue={searchQuery}
        id={comboboxId}
        onOpen={() => {
          setIsOpen(true);
          onSearchQueryChange('');
        }}
        onClose={() => {
          setIsOpen(false);
          setActiveIndex(-1);
          setActiveDescendant('');
        }}
        onInputChange={(_: any, value: string) => {
          onSearchQueryChange(value);
        }}
        onChange={(_: any, value: LearningOption | null) => {
          onSelectionChange(value?.value || 0, searchQuery, value?.type || null);
        }}
        ListboxProps={{
          id: listboxId,
          'aria-label': 'Learning content options',
          role: 'listbox',
          ref: listRef,
        }}
        componentsProps={{
          paper: {
            elevation: 8,
            'aria-describedby': 'search-results-description',
          },
        }}
        renderInput={(
          params: JSX.IntrinsicAttributes & { variant?: TextFieldVariants | undefined } & Omit<
              FilledTextFieldProps | OutlinedTextFieldProps | StandardTextFieldProps,
              'variant'
            >,
        ) => (
          <TextField
            {...params}
            label="Search learning content"
            variant="outlined"
            inputRef={(input) => {
              if (input) {
                inputRef.current = input;
                if (params.InputProps?.ref && 'current' in params.InputProps.ref) {
                  const ref = params.InputProps.ref as React.MutableRefObject<unknown>;
                  ref.current = input;
                }
              }
            }}
            InputProps={{
              ...params.InputProps,
              inputProps: {
                ...params.inputProps,
                'aria-autocomplete': 'list',
                'aria-controls': isOpen ? listboxId : undefined,
                'aria-activedescendant': activeDescendant || undefined,
                'aria-describedby': 'search-description',
              },
              endAdornment: (
                <>
                  {searchLoading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{ color: '#019df2' }}
                      aria-label="Loading results"
                    />
                  ) : null}
                  {params.InputProps?.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={renderCustomOption}
      />
      <Box id="search-description" className="sr-only" sx={{ display: 'none' }}>
        Start typing to search for learning content. Use up and down arrows to navigate results,
        Enter to select, and Escape to close the dropdown.
      </Box>
      <Box id="search-results-description" className="sr-only" sx={{ display: 'none' }}>
        Results will appear as you type. Navigate through results using arrow keys.
      </Box>
    </Box>
  );
};
